// Tier-2 <DatePicker> = label + field + error, backed by AntD DatePicker (FORT-331).
// Mirrors the Select/Input form contract (label/field/error column, React.useId()
// for label association, status on error) so it drops into an AntD <Form.Item>.
// Values are dayjs objects — the app's date library. Label/error spacing + colour
// come from tokens.
import React from 'react';
import { DatePicker as AntDatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import { useTokens } from '../../core/theme/TokensProvider';
import type { CommonProps } from './types';

export type DatePickerGranularity = 'date' | 'week' | 'month' | 'quarter' | 'year';

export interface DatePickerProps extends CommonProps {
  label?: string;
  error?: string;
  value?: Dayjs | null;
  defaultValue?: Dayjs | null;
  onChange?: (value: Dayjs | null, dateString: string | string[]) => void;
  placeholder?: string;
  /** Display/parse format, e.g. "DD/MM/YYYY". */
  format?: string;
  disabled?: boolean;
  allowClear?: boolean;
  picker?: DatePickerGranularity;
  id?: string;
  /** Per-date predicate — return true to grey a date out (e.g. no dates in the past). */
  disabledDate?: (current: Dayjs) => boolean;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  error,
  value,
  defaultValue,
  onChange,
  placeholder,
  format,
  disabled,
  allowClear = true,
  picker,
  id,
  disabledDate,
  className,
  style,
  'data-testid': dataTestId,
}) => {
  const tokens = useTokens();
  const generatedId = React.useId();
  const pickerId = id ?? generatedId;
  return (
    <div
      className={className}
      style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[1], ...style }}
    >
      {label && (
        <label
          htmlFor={pickerId}
          style={{ fontSize: tokens.fontSize.sm, fontWeight: tokens.fontWeight.medium }}
        >
          {label}
        </label>
      )}
      <AntDatePicker
        id={pickerId}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        placeholder={placeholder}
        format={format}
        disabled={disabled}
        allowClear={allowClear}
        picker={picker}
        disabledDate={disabledDate}
        status={error ? 'error' : undefined}
        style={{ width: '100%' }}
        data-testid={dataTestId}
      />
      {error && (
        <span style={{ fontSize: tokens.fontSize.sm, color: tokens.color.danger }}>{error}</span>
      )}
    </div>
  );
};
